import React, { Suspense, lazy, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  Brain,
  Calculator,
  FileText,
  Landmark,
  MessageCircle,
  Scale,
  ShieldCheck,
  Sparkles,
  Table2,
  Truck,
  Users,
} from 'lucide-react';

import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { getAgentRoutePath, getAssignedAgentDetails, getAssignedAgents, getUserRole, isAgentOnlyAccount, isPositiveAgentSeat } from '../lib/access';
import { SidebarContext } from '../components/AppSidebar';

const platformAgentCodes = ['image_i'];
const iconByKey = {
  bot: Bot,
  chat: MessageCircle,
  doc: FileText,
  sheet: Table2,
  tax: Calculator,
  law: Scale,
  ship: Truck,
  shield: ShieldCheck,
  bank: Landmark,
  chart: BarChart3,
  people: Users,
  brain: Brain,
};

const fallbackAgentRoutes = {
  chat_widget: '/imagei/agents/chat',
  document_agent: '/imagei/agents/document',
  shipping_document_agent: '/imagei/agents/shipping',
  spreadsheet_ai: '/imagei/agents/spreadsheet',
  labour_law_advisor: '/imagei/agents/labour',
  compliance_agent: '/imagei/agents/compliance',
  tax_agent: '/imagei/agents/tax',
};

const agentPageByCode = {
  chat_widget: lazy(() => import('../../agents/chatwidget/ChatWidgetPage')),
  document_agent: lazy(() => import('../../agents/documentagent/DocumentAgentPage')),
  shipping_document_agent: lazy(() => import('../../agents/shippingdocumentagent/ShippingDocumentAgentPage')),

  spreadsheet_ai: lazy(() => {
    console.log("Loading spreadsheet_ai module...");
    return import('../../agents/spreadsheetai/SpreadsheetAIPage')
      .then((module) => {
        console.log("spreadsheet_ai module loaded:", module);
        return module;
      })
      .catch((err) => {
        console.error("spreadsheet_ai module failed:", err);
        throw err;
      });
  }),

  labour_law_advisor: lazy(() => import('../../agents/labourlawadvisor/LabourLawAdvisorPage')),
  compliance_agent: lazy(() => import('../../agents/complianceagent/ComplianceAgentPage')),
  tax_agent: lazy(() => import('../../agents/taxagent/TaxAgentPage')),
};

function formatAgentName(code) {
  return String(code || '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalizeAgentRoute(route) {
  if (!route) return null;
  const value = String(route).trim();
  if (!value) return null;
  if (value.startsWith('/')) return value;
  if (value.startsWith('imagei/')) return `/${value}`;
  if (value.startsWith('agents/')) return `/imagei/${value}`;
  return `/imagei/agents/${value}`;
}

function getConfiguredRoute(agent) {
  return normalizeAgentRoute(
    agent?.route_path ||
    agent?.frontend_path ||
    agent?.page_path ||
    agent?.path ||
    agent?.route ||
    agent?.url
  );
}

function toPascalCase(code = '') {
  return String(code)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

function MissingAgentPage({ agentCode }) {
  return (
    <div className="px-8 py-10">
      <h1 className="m-0 text-2xl font-semibold text-foreground">Agent frontend not configured</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        No frontend file was found for {agentCode}. Add frontend code from Manage Subscription and rebuild or restart the frontend.
      </p>
    </div>
  );
}

function CustomAgentPageLoader({ agentCode }) {
  const [CustomPage, setCustomPage] = useState(null);

  React.useEffect(() => {
    let mounted = true;
    const folderName = String(agentCode || '').toLowerCase().trim().replace(/_/g, '');
    const componentName = `${toPascalCase(agentCode)}Page`;

    setCustomPage(null);

    import(
      /* webpackInclude: /Page\.js$/ */
      `../../agents/${folderName}/${componentName}.js`
    )
      .then((module) => {
        if (!mounted) return;
        setCustomPage(() => module.default || (() => <MissingAgentPage agentCode={agentCode} />));
      })
      .catch(() => {
        if (!mounted) return;
        setCustomPage(() => () => <MissingAgentPage agentCode={agentCode} />);
      });

    return () => {
      mounted = false;
    };
  }, [agentCode]);

  if (!CustomPage) {
    return <div className="px-8 py-10 text-center text-muted-foreground">Loading agent...</div>;
  }

  return <CustomPage />;
}

export function AgentsPage() {
  console.log("🔥 AgentsPage latest code loaded");
  const account = useSelector((state) => state.account || {});
  const history = useHistory();
  const { setCollapsed } = useContext(SidebarContext);
  const agentOnly = isAgentOnlyAccount(account);
  const isSuperAdmin = getUserRole(account) === 'Super Admin';
  const agentDetails = getAssignedAgentDetails(account);
  const detailByCode = new Map(agentDetails.map((agent) => [agent.code || agent.agent_code, agent]));
  const assignedAgents = getAssignedAgents(account);
  const orgAgentPools = Array.isArray(account.org_agent_seat_pools) ? account.org_agent_seat_pools : [];

  const userAssignedAgentCards = Object.keys(assignedAgents)
    .filter((code) => !platformAgentCodes.includes(code) && isPositiveAgentSeat(assignedAgents[code]))
    .map((code) => {
      const detail = detailByCode.get(code);
      return {
        id: code,
        code,
        title: detail?.name || formatAgentName(code),
        description: detail?.description || 'Custom AI workspace assigned to your account',
        Icon: iconByKey[detail?.icon] || Sparkles,
        route:
          code === "spreadsheet_ai"
            ? "/imagei/agents/spreadsheet"
            : getConfiguredRoute(detail) || fallbackAgentRoutes[code] || getAgentRoutePath(account, code),
      };
    });

  const orgAssignedAgentCards = orgAgentPools
    .filter((agent) => {
      const code = agent?.code || agent?.agent_code;
      return (
        code &&
        !platformAgentCodes.includes(code) &&
        agent.is_active !== false &&
        isPositiveAgentSeat(
          agent.seats_purchased ??
          agent.seats_total ??
          agent.totalSeats ??
          agent.seats
        )
      );
    })
    .map((agent) => {
      const code = agent.code || agent.agent_code;
      return {
        id: code,
        code,
        title: agent.name || detailByCode.get(code)?.name || formatAgentName(code),
        description: agent.description || detailByCode.get(code)?.description || 'AI workspace assigned to this organization',
        Icon: iconByKey[agent.icon] || iconByKey[detailByCode.get(code)?.icon] || Sparkles,
        route: getConfiguredRoute(agent) || getConfiguredRoute(detailByCode.get(code)) || fallbackAgentRoutes[code] || getAgentRoutePath(account, code),
      };
    });

  const visibleAgents =
    isSuperAdmin && orgAssignedAgentCards.length > 0
      ? orgAssignedAgentCards
      : userAssignedAgentCards;
  console.log("USER ASSIGNED AGENT CARDS:", userAssignedAgentCards);
  console.log("ORG ASSIGNED AGENT CARDS:", orgAssignedAgentCards);
  console.log("VISIBLE AGENTS:", visibleAgents);
  console.log(
    "SPREADSHEET CARD:",
    visibleAgents.find((a) => a.code === "spreadsheet_ai")
  );

  const [selectedAgent, setSelectedAgent] = useState(null);
  const SelectedAgentPage = selectedAgent ? agentPageByCode[selectedAgent.code] : null;
  const openAgentWorkspace = (agent) => {
    console.log("OPEN AGENT:", agent);

    setCollapsed(true);

    if (agent.route) {
      console.log("NAVIGATING TO:", agent.route);
      history.push(agent.route);
      return;
    }

    setSelectedAgent(agent);
  };
  console.log("ACCOUNT", account);
  console.log("USER ROLE", getUserRole(account));
  console.log("AGENT DETAILS", agentDetails);
  console.log("DETAIL BY CODE", [...detailByCode.entries()]);
  console.log("ASSIGNED AGENTS", assignedAgents);
  console.log("ORG AGENT POOLS", orgAgentPools);
  console.log("SELECTED AGENT:", selectedAgent);
  console.log("SELECTED AGENT PAGE:", SelectedAgentPage); 
  
  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-background">
      <PageHeader
        eyebrow="AI Workspace"
        title="IMAGE-I Agent"
        description="One agent, multiple skills - switch context to get specialized analysis."
      />

      {visibleAgents.length > 0 ? (
        <div className="flex flex-col lg:flex-row">
          {/* Skills sidebar */}
          <div className={cn(
            'w-full border-b border-border/60 bg-[#fbfdfc] px-4 py-6 lg:w-[320px] lg:border-b-0 lg:border-r lg:px-5',
            selectedAgent && 'hidden'
          )}>
            <p className="px-1 pb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Skills
            </p>
            <div className="flex flex-col gap-3">
              {visibleAgents.map((agent) => {
                console.log("RENDERING AGENT BUTTON:", agent.code, agent);
                return (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => openAgentWorkspace(agent)}
                    className={cn(
                      'group flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all duration-200',
                      'border-[#d7e6e1] bg-white hover:-translate-y-0.5 hover:border-[#00a174]/45 hover:shadow-[0_10px_24px_rgba(7,26,45,0.08)]'
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg',
                        'bg-[#e9f8f2] text-[#087a5d]'
                      )}
                    >
                      <agent.Icon className="h-4.5 w-4.5" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-semibold text-foreground">
                        {agent.title}
                      </span>
                      <span className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                        {agent.description}
                      </span>
                    </span>
                  </button>
                  );
              })}
            </div>

            {!agentOnly && (
              <div className="mt-6">
                <Button variant="outline" className="w-full" onClick={() => history.push('/imagei/overview')}>
                  Back to overview
                </Button>
              </div>
            )}
          </div>

          <div className="flex min-h-[calc(100vh-13rem)] flex-1 flex-col border-t border-border/60">
            {selectedAgent ? (
              <>
                <div className="px-4 py-3 lg:px-6">
                  <Button variant="outline" onClick={() => setSelectedAgent(null)}>
                    Back to agents
                  </Button>
                </div>
                <Suspense fallback={<div className="px-8 py-10 text-center text-muted-foreground">Loading agent...</div>}>
                  {SelectedAgentPage ? (
                    <SelectedAgentPage />
                  ) : (
                    <CustomAgentPageLoader agentCode={selectedAgent.code} />
                  )}
                </Suspense>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center px-6 py-16 text-sm text-muted-foreground">
                Select an agent to open its workspace.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border-b border-border/60 bg-[#fbfdfc] px-6 py-9 lg:px-10">
          <div className="max-w-[760px] rounded-xl border border-dashed border-[#c8d8d3] bg-white p-10 text-center shadow-[0_14px_45px_rgba(7,26,45,0.06)]">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-xl bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <p className="mt-4 text-base font-semibold text-foreground">No agents assigned</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Ask your administrator to assign an agent license from User Allocation.
            </p>
          </div>

          {!agentOnly && (
            <div className="mx-auto mt-6 max-w-[760px]">
              <Button variant="outline" onClick={() => history.push('/imagei/overview')}>
                Back to overview
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
